import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useCompany } from "@/hooks/useCompany";
import { usePackageAccess } from "@/lib/packageAccess";
import { getWallet } from "@/lib/credits/api";

export const TRIAL_READ_ONLY_CODE = "TRIAL_READ_ONLY";
export const TRIAL_READ_ONLY_MESSAGE =
  "Your free Delapresse account has used its included credits. Choose a plan to keep creating, publishing and connecting sites.";
/** One-time credit grant of the free tier (see verify-email-code). */
export const FREE_TIER_CREDITS = 30;

export function useTrialMode() {
  const { user } = useAuth();
  const { currentCompanyId } = useCompany();
  const packageAccess = usePackageAccess();
  const queryClient = useQueryClient();
  const pkg = packageAccess.package;
  const isTrialPackage = Boolean(pkg?.is_trial || pkg?.slug?.replace(/-v2$/, "") === "trial");

  // Free tier: the workspace stays writable while the one-time credit grant
  // lasts. Mirrors public.company_is_trial() in the database.
  const { data: wallet, isLoading: walletLoading } = useQuery({
    queryKey: ["free-tier-wallet", currentCompanyId],
    queryFn: () => getWallet(currentCompanyId!),
    enabled: !!currentCompanyId && isTrialPackage,
    staleTime: 30 * 1000,
  });
  const freeCreditsLeft = Math.max(0, Number(wallet?.balance ?? 0));
  const isReadOnly = isTrialPackage && !packageAccess.isAdmin && !walletLoading && freeCreditsLeft <= 0;

  const { data: dismissedAt = null, isLoading: dismissalLoading } = useQuery({
    queryKey: ["trial-reminder-dismissed-at", user?.id],
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from("profiles")
        .select("trial_reminder_dismissed_at")
        .eq("id", user!.id)
        .maybeSingle();
      if (error) throw error;
      return data?.trial_reminder_dismissed_at ?? null;
    },
    enabled: !!user,
  });

  const dismissReminder = useMutation({
    mutationFn: async () => {
      const dismissed = new Date().toISOString();
      const { error } = await (supabase as any)
        .from("profiles")
        .update({ trial_reminder_dismissed_at: dismissed })
        .eq("id", user!.id);
      if (error) throw error;
      return dismissed;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["trial-reminder-dismissed-at", user?.id] });
    },
  });

  const showReminderAgain = useMutation({
    mutationFn: async () => {
      const { error } = await (supabase as any)
        .from("profiles")
        .update({ trial_reminder_dismissed_at: null })
        .eq("id", user!.id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["trial-reminder-dismissed-at", user?.id] });
    },
  });

  return {
    package: pkg,
    currentCompanyId,
    isLoading: packageAccess.isLoading || dismissalLoading || (isTrialPackage && walletLoading),
    freeCreditsLeft,
    isTrialPackage,
    isReadOnly,
    canManageRealData: !isReadOnly,
    reminderDismissed: Boolean(dismissedAt) || !isTrialPackage,
    reminderDismissedByUser: Boolean(dismissedAt),
    dismissReminder,
    showReminderAgain,
  };
}
